// ======================
// ID 工具
// ======================
export function createLocalId(prefix = 'local') {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

// ======================
// Guard 工具
// ======================
export function asArray(value) {
  return Array.isArray(value) ? value : []
}

export function asRecord(value) {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : {}
}

export function pickFirstString(record, keys) {
  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'string' && value.trim()) {
      return value
    }
  }
  return ''
}

// ======================
// <RESPONSE> 结构化响应解析
// ======================
function sanitizeJsonLikeString(raw) {
  // 1. 去掉 LLM 可能包裹值的反引号
  let str = raw.trim().replace(/`/g, '')

  // 2. 修复字符串内部未转义的双引号（如 message 中包含 "..."）
  // 在 JSON 字符串中遇到 " 时，如果后续紧跟 : , } ] 或字符串结尾，则视为字符串结束；
  // 否则视为内容中的引号，前面补反斜杠转义。
  let result = ''
  let inString = false
  let escaped = false
  for (let i = 0; i < str.length; i++) {
    const char = str[i]
    if (inString) {
      if (escaped) {
        result += char
        escaped = false
        continue
      }
      if (char === '\\') {
        result += char
        escaped = true
        continue
      }
      if (char === '"') {
        let j = i + 1
        while (j < str.length && /\s/.test(str[j])) j++
        const next = str[j]
        const isEndQuote = !next || [':', ',', '}', ']'].includes(next)
        if (isEndQuote) {
          result += char
          inString = false
        } else {
          result += '\\"'
        }
        continue
      }
      result += char
    } else {
      result += char
      if (char === '"') inString = true
    }
  }
  return result
}

function parseStructuredPayload(rawJson) {
  const sanitized = sanitizeJsonLikeString(rawJson)

  try {
    const json = JSON.parse(sanitized)
    const options = Array.isArray(json.options)
      ? json.options.filter((item) => typeof item === 'string' && item.trim())
      : []
    const doctors = Array.isArray(json.doctors)
      ? json.doctors.filter((item) => item && typeof item === 'object')
      : []
    const packages = Array.isArray(json.packages)
      ? json.packages.filter((item) => item && typeof item === 'object')
      : []
    const nextActions = Array.isArray(json.next_actions)
      ? json.next_actions.filter((item) => typeof item === 'string' && item.trim())
      : []
    return {
      type: json.type || '',
      message: typeof json.message === 'string' ? json.message.trim() : '',
      options,
      doctors,
      packages,
      next_actions: nextActions,
    }
  } catch (error) {
    console.error('[parseResponseWrapper] parse failed:', error, sanitized)

    // 兜底：即使整体 JSON 无法解析，也尽量把 message 文案展示出来
    const messageMatch = sanitized.match(/"message"\s*:\s*"([\s\S]*?)"/)
    if (messageMatch) {
      return {
        type: '',
        message: messageMatch[1].replace(/\\"/g, '"').trim(),
        options: [],
        doctors: [],
        packages: [],
        next_actions: [],
      }
    }

    return null
  }
}

export function parseResponseWrapper(content) {
  if (typeof content !== 'string') return null

  const match = content.match(/<RESPONSE>([\s\S]*?)<\/RESPONSE>/)
  if (match) {
    return parseStructuredPayload(match[1])
  }

  // 兼容未包裹 <RESPONSE> 标签的裸 JSON 输出（如智能导检直接输出结构体）。
  // 要求带非空 type 字段，避免把恰好以 { 开头的普通文本误判为结构化响应。
  const trimmed = content.trim()
  if (trimmed.startsWith('{')) {
    const parsed = parseStructuredPayload(trimmed)
    if (parsed && parsed.type) return parsed
  }

  return null
}

// ======================
// <滇医通业务信息> instructions 构建（体检中心知识库精准检索）
// v2 协议：旧字段 hospital_info / hospital_id / hospital_name 停止使用
// ======================
export function buildCheckupCenterInstructions(checkupCenterId, checkupCenterName) {
  const id = typeof checkupCenterId === 'string' ? checkupCenterId.trim() : ''
  if (!id) return '' // 未限定体检中心时省略整个标签，Agent 全局检索

  const name = typeof checkupCenterName === 'string' ? checkupCenterName.trim() : ''
  const checkupCenterInfo = { checkup_center_id: id, ...(name ? { checkup_center_name: name } : {}) }

  return `<滇医通业务信息>\n${JSON.stringify({ checkup_center_info: checkupCenterInfo })}\n</滇医通业务信息>`
}

// ======================
// URL 参数解码（中文医院名等可能被 url 转义）
// ======================
/**
 * 安全解码 URL query 中可能被转义的值。
 * - 兼容单次/双重 encodeURIComponent 编码（Vue Router 只解一层，双重编码会残留 %XX）
 * - 无转义序列时原样返回，零开销
 * - 遇到非法转义序列（如 "100%折扣" 中的 %折）不抛错，保留原值
 */
export function safeDecodeUrlValue(value) {
  if (typeof value !== 'string') return value

  let result = value
  for (let i = 0; i < 2; i += 1) {
    if (!/%[0-9A-Fa-f]{2}/.test(result)) break
    let decoded
    try {
      decoded = decodeURIComponent(result)
    } catch (error) {
      break // 非法转义序列，保留原值
    }
    if (decoded === result) break
    result = decoded
  }
  return result
}

// ======================
// 时间 工具
// ======================
export function formatMessageTime(input) {
  if (!input) return ''

  const date = new Date(input)
  if (Number.isNaN(date.getTime())) return input

  return `${date.getHours().toString().padStart(2, '0')}:${date
    .getMinutes()
    .toString()
    .padStart(2, '0')}`
}
